import { useRef } from 'react';
import { LOGO_URL } from '../../lib/defaultData';

// Use local logo for any external/old URLs; only trust base64 uploads and local paths
function resolveLogoUrl(url) {
  if (!url) return LOGO_URL;
  if (url.startsWith('data:')) return url;       // user-uploaded base64 — keep as-is
  if (url.startsWith('http')) return LOGO_URL;   // old CDN or external URL — use new logo
  return url;                                     // local path like /LOGO.jpg or /logo.png
}

export default function CompanyHeader({ companyInfo, onUpdate }) {
  const fileInputRef = useRef(null);

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      onUpdate('logo_url', ev.target.result);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div
      className="flex items-start justify-between p-8 md:p-10"
      style={{ background: '#1a1a2e' }}
      data-testid="company-header"
    >
      {/* Left: Logo + Company Name */}
      <div className="flex items-center gap-6">
        <div
          className="cursor-pointer flex-shrink-0 w-[120px] h-[120px] flex items-center justify-center rounded overflow-hidden"
          style={{ border: '1px dashed rgba(201, 168, 76, 0.4)' }}
          onClick={() => fileInputRef.current?.click()}
          data-testid="logo-upload-area"
        >
          <img
            src={resolveLogoUrl(companyInfo.logo_url)}
            alt="Company Logo"
            className="max-w-full max-h-full object-contain"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg"
            className="hidden"
            onChange={handleLogoUpload}
            data-testid="logo-file-input"
          />
        </div>
        <div>
          <input
            value={companyInfo.name}
            onChange={e => onUpdate('name', e.target.value)}
            className="editable-field editable-field-dark text-2xl font-bold tracking-tight bg-transparent"
            style={{ fontFamily: "'Space Grotesk', sans-serif", color: 'white' }}
            data-testid="company-name-input"
          />
          <input
            value={`ACN: ${companyInfo.acn}`}
            onChange={e => onUpdate('acn', e.target.value.replace('ACN: ', ''))}
            className="editable-field editable-field-dark text-sm mt-1 bg-transparent"
            style={{ color: 'rgba(255,255,255,0.7)' }}
            data-testid="company-acn-input"
          />
        </div>
      </div>

      {/* Right: Contact Info */}
      <div className="text-right space-y-1">
        <textarea
          value={companyInfo.address}
          onChange={e => onUpdate('address', e.target.value)}
          placeholder="Company Address"
          rows={2}
          className="editable-field editable-field-dark text-sm text-right bg-transparent resize-none w-[260px]"
          style={{ color: 'rgba(255,255,255,0.85)' }}
          data-testid="company-address-input"
        />
        <input
          value={companyInfo.phone}
          onChange={e => onUpdate('phone', e.target.value)}
          className="editable-field editable-field-dark text-sm text-right bg-transparent w-full"
          style={{ color: 'rgba(255,255,255,0.85)' }}
          data-testid="company-phone-input"
        />
        <input
          value={companyInfo.director}
          onChange={e => onUpdate('director', e.target.value)}
          className="editable-field editable-field-dark text-sm text-right bg-transparent w-full"
          style={{ color: '#c9a84c' }}
          data-testid="company-director-input"
        />
      </div>
    </div>
  );
}
